import { AfterViewInit, Component, ElementRef, Input, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { isPlatformBrowser, NgClass, NgTemplateOutlet } from '@angular/common';
import { LocalStorageService } from 'src/app/modules/shared/services/storage/local-storage.service';
import { StateService } from 'src/app/modules/shared/services/storage/state.service';
import {
  MatDialog,
  MatDialogRef,
  MatDialogState
} from '@angular/material/dialog';
import { PanelService } from 'src/app/modules/shared/services/api/panel.service';
import { CountryDetail } from 'src/app/modules/shared/models/profile.mode';
import { StoreRegionService } from 'src/app/modules/shared/services/store-region.service';
import { PreferenceService } from 'src/app/modules/shared/services/preference/preference.service';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
// import { TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'src/app/modules/shared/services/device-detector/device-detector.service';
import { KingClubAssets } from 'src/app/modules/shared/enum/icons';
import { Url_Friendly_String } from 'src/app/modules/shared/helpers/helper';
import { SearchFieldComponent } from '../search-field/search-field.component';
import { MatAnchor, MatButtonModule } from '@angular/material/button';
import { StoreCategoryV2 } from '../../shared/models/category.model';

@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    standalone: true,
    imports: [NgClass, NgTemplateOutlet, SearchFieldComponent, MatAnchor, RouterLink, MatButtonModule, RouterLinkActive]
})
export class NavigationComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private stateService = inject(StateService);
  private localStorageService = inject(LocalStorageService);
  private dialog = inject(MatDialog);
  private panelService = inject(PanelService);
  private storeRegionService = inject(StoreRegionService);
  private preferenceService = inject(PreferenceService);
  private deviceDetectorService = inject(DeviceDetectorService);
  // private translateService = inject(TranslateService);
  private platformId = inject(PLATFORM_ID);
  @Input() isClubMember: boolean;
  @ViewChild('categoriesDialog') categoriesDialog: any;
  isLoggedIn: boolean = false;
  isSearchOpened: boolean;
  isDesktop: boolean = true;
  hideNavigation: boolean = false;
  hideControllers: boolean = false;
  dialogRef: MatDialogRef<any>;
  categories: any;
  categoryDialogIsOpen: boolean;
  selectedRegion: CountryDetail;
  @ViewChild('preferencesDialog') preferencesDialog: any;
  @ViewChild('headerMobile') headerMobileElement: ElementRef;

  throneBadge = KingClubAssets.ThroneBadgeWhite;
  hoveredCategoryIndex = -1;
  constructor() {}
  
  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.deviceDetectorService
        .$breakpoint()
        .subscribe(sizes => this.isDesktop = sizes.matches);
    }
    // this.translateService.setFallbackLang('en');
    // this.translateService.use('en');
    this.selectedRegion = this.storeRegionService.selectedRegion;
    this.checkRoutes();
    // Only fetch categories in browser and if selectedRegion is available
    if (isPlatformBrowser(this.platformId)) {
      if (this.selectedRegion?.code) {
        this.getCategoryList();
      } else {
        // Wait for region to be available via observable
        this.storeRegionService.selectedRegion$.subscribe((region) => {
          if (region?.code && !this.categories) {
            this.selectedRegion = region;
            this.getCategoryList();
          }
        });
      }
    }
    
    this.hideNavigation = this.isNoneNavigationRoute;
    this.hideControllers = this.isCryptoRoute;
    this.setLoginStatus();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.hideNavigation = this.isNoneNavigationRoute;
        this.hideControllers = this.isCryptoRoute;
        this.setLoginStatus();
        if(isPlatformBrowser(this.platformId)){
          if (event.urlAfterRedirects.includes('profile')) {
            this.headerMobileElement?.nativeElement.classList.add('blue-bg');
          } else {
            this.headerMobileElement?.nativeElement.classList.remove('blue-bg');
          }
        }
      }
    });

    this.stateService.getPreferenceRegionShouldUpdate().subscribe((region) => {
      if (region) {
        this.selectedRegion = region;
        // Refresh categories when region changes
        if (region?.code) {
          this.getCategoryList();
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // if (!isPlatformBrowser(this.platformId)) return;
    if (this.router?.url?.includes('profile')) {
      this.headerMobileElement?.nativeElement.classList.add('blue-bg');
    }
  }

  openCategories() {
    this.closeDialog();

    this.dialogRef = this.dialog.open(this.categoriesDialog, {
      autoFocus: false,
      restoreFocus: false,
      position: {
        top: '72px',
        left: '260px'
      },
      panelClass: ['dialog-full-width', 'category-dialog'],
      backdropClass: ['transparent-dialog-backdrop']
    });
    this.categoryDialogIsOpen = true;
    this.dialogRef.afterClosed().subscribe((data) => {
      this.categoryDialogIsOpen = false;
    });
  }

  openPreference() {
    this.preferenceService.openPreference();
  }

  searchOnFocus(state: boolean) {
    this.isSearchOpened = state;
  }

  private get isNoneNavigationRoute() {
    return (
      this.router.url.includes('/authenticate') ||
      this.router.url.includes('/payment')
    );
  }

  private get isCryptoRoute() {
    return this.router.url.includes('/crypto');
  }

  private getCategoryList() {
    // Skip during SSR
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    
    // Ensure selectedRegion is available before making the request
    const region = this.storeRegionService.selectedRegion || this.selectedRegion;
    if (!region?.code) {
      return;
    }

    this.panelService
      .getCategoryListV2({ is_collection: false })
      .subscribe({
        next: (categories: StoreCategoryV2[]) => {
          let newCategories = [
            {
              id: 0,
              name: 'All',
              url: `/category/${region.code}/all`,
              is_active: false,
              avatar: 'https://d1upatzsvnpphr.cloudfront.net/broker/notification_system/media/file/all-categories.svg'
            }
          ];

          const blackfridayCategoryIndex = categories.findIndex(
            (category) => category.name.toLowerCase() === 'black friday'
          );

          const categoriesWithoutBlackFriday = categories.filter(
            (cat) => cat.name.toLowerCase() !== 'black friday'
          );
          if (blackfridayCategoryIndex > -1) {
            const blackfridayCategory = {
              ...categories[blackfridayCategoryIndex],
              id: categories[blackfridayCategoryIndex].category_id,
              url: `/category/${region.code}/${categories[blackfridayCategoryIndex].category_id}/${this.getCategoryUrlFriendlyName(categories[blackfridayCategoryIndex].name)}`,
              cssClass: 'black-friday-category',
              avatar: '/images/icons/bf-icon.webp',
              isBlackFriday: true,
              is_active: false
            };
            newCategories = [...newCategories, blackfridayCategory];
          }
          this.categories = [
            ...newCategories,

            ...categoriesWithoutBlackFriday?.map((c) => ({
              ...c,
              id: c.category_id,
              url: `/category/${region.code}/${c.category_id}/${this.getCategoryUrlFriendlyName(c.name)}`
            }))
          ];
        },
        error: (error) => {
          // Don't block route loading if category fetch fails
        }
      });
  }

  private getCategoryUrlFriendlyName(categoryName: string) {
    return Url_Friendly_String(categoryName);
  }

  closeDialog() {
    if (this.dialogRef && this.dialogRef.getState() !== MatDialogState.CLOSED) {
      this.dialogRef.close();
      this.categoryDialogIsOpen = false;
    }
  }

  private checkRoutes() {
    this.stateService.isSearching.subscribe((isSearch) => {
      if (!this.isDesktop) {
        this.isSearchOpened = isSearch;
      }
    });
  }

  private setLoginStatus() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.isLoggedIn = this.localStorageService.isLoggedIn;
    this.stateService.setLoginStatus(
       this.localStorageService.isLoggedIn
    );
  }
}
